import { Component } from '@theme/component';

class RetailCarousel extends Component {
  requiredRefs = ['track'];
  #controller; #timer; #observer; #visible = true; #hovering = false;
  #reduced = matchMedia('(prefers-reduced-motion: reduce)');
  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort(); this.#observer?.disconnect();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.refs.track.addEventListener('scroll', () => this.#updateDots(), options);
    this.addEventListener('pointerenter', event => { if(event.pointerType === 'mouse'){this.#hovering = true;this.#stop();} }, options);
    this.addEventListener('pointerleave', () => {this.#hovering = false;this.#start();}, options);
    this.addEventListener('focusin', () => this.#stop(), options);
    this.addEventListener('focusout', () => queueMicrotask(() => {if(this.isConnected)this.#start();}), options);
    document.addEventListener('visibilitychange', () => this.#start(), options);
    document.addEventListener('nr:motion-change', () => this.#start(), options);
    this.#reduced.addEventListener('change', () => this.#start(), options);
    if('IntersectionObserver' in window){this.#observer = new IntersectionObserver(entries => {this.#visible = entries[0].isIntersecting;this.classList.toggle('nr-hero-offscreen',!this.#visible);this.#start();},{threshold:0});this.#observer.observe(this);}
    this.#updateDots();this.#start();
  }
  disconnectedCallback() { super.disconnectedCallback();this.#controller?.abort();this.#observer?.disconnect();this.#stop(); }
  #stop() { clearInterval(this.#timer);this.#timer = undefined; }
  #paused() {return this.#reduced.matches || document.hidden || !document.documentElement.classList.contains('nr-motion-ready') || document.documentElement.classList.contains('nr-motion-paused');}
  #start() {
    this.#stop();
    if(this.dataset.auto === 'true' && !this.#paused() && this.#visible && !this.#hovering && !this.contains(document.activeElement)) this.#timer = setInterval(() => this.next(),6500);
  }
  #move(direction) {
    const track = this.refs.track;
    if(!track.clientWidth)return;
    const step = this.classList.contains('nr-hero') ? track.clientWidth : Math.max(track.clientWidth * .8,200);
    let left = track.scrollLeft + step * direction;
    if(this.classList.contains('nr-hero')){if(left > track.scrollWidth-track.clientWidth+8)left=0;if(left<0)left=track.scrollWidth-track.clientWidth;}
    track.scrollTo({left,behavior:this.#paused()?'instant':'smooth'});
  }
  next(){this.#move(1);}
  previous(){this.#move(-1);}
  goTo(event){const button=event.target.closest('[data-slide]');if(button)this.refs.track.scrollTo({left:Number(button.dataset.slide)*this.refs.track.clientWidth,behavior:this.#paused()?'instant':'smooth'});}
  #updateDots(){const width=this.refs.track.clientWidth;if(!width)return;const index=Math.round(this.refs.track.scrollLeft/width);this.querySelectorAll('[data-slide]').forEach(button=>{if(Number(button.dataset.slide)===index)button.setAttribute('aria-current','true');else button.removeAttribute('aria-current');});this.querySelectorAll('.nr-hero__slide').forEach((slide,i)=>slide.dataset.active=String(i===index));}
}
class RetailHeader extends Component {
  #controller;
  connectedCallback() {
    super.connectedCallback(); this.#controller = new AbortController(); const options={signal:this.#controller.signal};
    this.querySelector('[data-close-announcement]')?.addEventListener('click',event=>event.currentTarget.closest('.nr-announcement').remove(),options);
    this.querySelectorAll('.nr-nav-group').forEach(details=>{
      details.addEventListener('pointerenter',event=>{if(event.pointerType==='mouse'){this.querySelectorAll('.nr-nav-group').forEach(other=>other.open=false);details.open=true;details.dataset.hoverOpened='true';}},options);
      details.querySelector('summary')?.addEventListener('click',event=>{if(event.detail>0 && details.dataset.hoverOpened==='true'){event.preventDefault();delete details.dataset.hoverOpened;details.open=true;}},options);
      details.addEventListener('pointerleave',()=>{delete details.dataset.hoverOpened;if(!details.contains(document.activeElement)) details.open=false;},options);
    });
    this.addEventListener('keydown',event=>{if(event.key==='Escape'){const open=[...this.querySelectorAll('details[open]')];const active=open.reverse().find(d=>d.contains(document.activeElement))||open[0];if(active){event.preventDefault();active.open=false;active.querySelector('summary')?.focus();}}},options);
    document.addEventListener('click',event=>{if(!this.contains(event.target))this.querySelectorAll('details[open]').forEach(d=>d.open=false);},options);
  }
  disconnectedCallback(){super.disconnectedCallback();this.#controller?.abort();}
}
const SAVED_KEY='nuzzle-retail-saved-v1';
function giftUrl(value){try{const url=new URL(value,location.origin);return url.origin===location.origin&&/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:pages|products)\//i.test(url.pathname)?url.pathname+url.search:null;}catch{return null;}}
function isSampleGift(url){try{return /^nr-sample-[a-z0-9-]+$/i.test(new URL(url,location.origin).searchParams.get('view')||'');}catch{return false;}}
function savedItems(){try{const list=JSON.parse(localStorage.getItem(SAVED_KEY)||'[]');if(!Array.isArray(list))return [];const seen=new Set();return list.flatMap(item=>{if(!item||typeof item.title!=='string')return [];const url=giftUrl(item.url);if(typeof item.image!=='string'||!item.image.trim())return [];let img;try{img=new URL(item.image,location.origin);if(!['https:','http:'].includes(img.protocol))return [];}catch{return [];}if(!url||seen.has(url))return [];seen.add(url);return [{url,sample:item.sample===true||isSampleGift(url),title:item.title.slice(0,300),price:item.price!==null&&item.price!==''&&Number.isFinite(Number(item.price))&&Number(item.price)>=0?Number(item.price):null,currency:/^[A-Z]{3}$/.test(item.currency)?item.currency:null,image:img.href}];}).slice(0,100);}catch{return [];}}
function writeSaved(items){try{localStorage.setItem(SAVED_KEY,JSON.stringify(items));document.dispatchEvent(new CustomEvent('nr:saved'));return true;}catch{document.dispatchEvent(new CustomEvent('nr:save-error'));return false;}}
class RetailStore extends Component {
  #controller;
  connectedCallback(){super.connectedCallback();this.#controller=new AbortController();const options={signal:this.#controller.signal};
    this.addEventListener('click',event=>{const button=event.target.closest('[data-save-gift]');if(!button)return;const url=giftUrl(button.dataset.saveGift);if(!url)return;let saved=savedItems();const removing=saved.some(x=>x.url===url);if(removing)saved=saved.filter(x=>x.url!==url);else saved.unshift({url,sample:button.dataset.giftSample==='true'||isSampleGift(url),title:button.dataset.giftTitle||'',price:Number(button.dataset.giftPrice),image:button.dataset.giftImage,currency:button.dataset.giftCurrency||this.dataset.currency});if(writeSaved(saved.slice(0,100)))this.#announce(removing?this.dataset.removed:this.dataset.added);else this.#announce(this.dataset.storageError);},options);
    document.addEventListener('nr:saved',()=>this.#refresh(),options);
    window.addEventListener('storage',event=>{if(event.key===SAVED_KEY)document.dispatchEvent(new CustomEvent('nr:saved'));},options);
    document.addEventListener('nr:saved-message',event=>this.#announce(event.detail?.action==='cleared'?this.dataset.cleared:this.dataset.removed),options);
    document.addEventListener('nr:save-error',()=>this.#announce(this.dataset.storageError),options);this.#refresh();
  }
  #announce(message){const status=this.querySelector('[data-saved-status]');if(status)status.textContent=message||'';}
  #refresh(){const saved=savedItems();this.querySelectorAll('[data-save-gift]').forEach(b=>{const pressed=saved.some(x=>x.url===giftUrl(b.dataset.saveGift));b.setAttribute('aria-pressed',String(pressed));const label=pressed?b.dataset.labelRemove:b.dataset.labelSave;if(label)b.setAttribute('aria-label',b.dataset.giftTitle?`${label}: ${b.dataset.giftTitle}`:label);const text=b.querySelector('[data-save-label]');if(text)text.textContent=(pressed?b.dataset.textSaved:b.dataset.textUnsaved)||label;});this.querySelectorAll('[data-saved-count]').forEach(c=>{c.textContent=String(saved.length);c.hidden=!saved.length;});}
  disconnectedCallback(){super.disconnectedCallback();this.#controller?.abort();}
}
class RetailCatalog extends Component {
  #controller;
  #form;
  #content;
  #panel;
  #dialog;
  #mobile;
  #snapshot = [];
  #session = false;
  #committed = false;
  #opener;
  #previousOverflow;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.#form = this.querySelector('[data-catalog-form]');
    if (!this.#form) return;
    this.#content = this.querySelector('[data-filter-content]');
    this.#panel = this.querySelector('[data-filter-panel]');
    this.#dialog = this.querySelector('[data-filter-dialog]');
    this.#mobile = matchMedia('(max-width: 749px)');
    this.#mobile.addEventListener('change', () => this.#syncFilterLayout(), options);
    this.#dialog?.addEventListener('cancel', (event) => { event.preventDefault(); this.#closeFilters(false); }, options);
    this.#dialog?.addEventListener('close', () => { if (!this.#dialog.open) this.#finishDialogClose(); }, options);
    this.#dialog?.addEventListener('click', (event) => {
      if (event.target !== this.#dialog) return;
      const bounds = this.#dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.#closeFilters(false);
    }, options);
    this.addEventListener('click', (event) => this.#handleClick(event), options);
    this.#form.addEventListener('input', (event) => {
      if (event.target.matches('[data-price-bound]')) this.#content.querySelectorAll('[data-price-bound]').forEach(input => input.setCustomValidity(''));
      if (this.#dialog?.open) this.#updateDraftCount();
    }, options);
    this.#form.addEventListener('change', (event) => {
      if (this.#dialog?.open) { this.#updateDraftCount(); return; }
      if (event.target.matches('[data-catalog-query]')) return;
      if (this.dataset.mode === 'native') { if (event.target.matches('[data-sort]')) this.#form.requestSubmit(); }
      else this.#apply(true);
    }, options);
    this.#form.addEventListener('submit', (event) => {
      if (!this.#validateRange()) { event.preventDefault(); return; }
      if (this.dataset.mode === 'sample') { event.preventDefault(); this.#apply(true); }
      this.#closeFilters(true);
    }, options);
    if (this.dataset.mode === 'sample') {
      this.querySelectorAll('.nr-card').forEach((card, index) => { card.dataset.original = String(index); });
      window.addEventListener('popstate', () => { this.#closeFilters(false); this.#read(); this.#apply(false); }, options);
      this.#read();
      this.#apply(false);
    } else {
      const sort = this.querySelector('[data-sort]');
      const current = new URL(location.href).searchParams.get('sort_by');
      if (current && [...sort.options].some(option => option.value === current)) sort.value = current;
      this.#refreshAppliedState();
    }
    this.#syncFilterLayout();
  }

  disconnectedCallback() {
    this.#closeFilters(false);
    if (this.#content && this.#panel) this.#panel.append(this.#content);
    if (this.#panel) this.#panel.hidden = false;
    this.querySelector('[data-open-filters]')?.setAttribute('hidden', '');
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  #handleClick(event) {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-open-filters]')) { this.#openFilters(event.target.closest('[data-open-filters]')); return; }
    if (event.target.closest('[data-close-filters]')) { this.#closeFilters(false); return; }
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const remove = event.target.closest('[data-remove-filter]');
    if (remove) {
      event.preventDefault();
      const index = [...this.querySelectorAll('[data-remove-filter]')].indexOf(remove);
      const url = this.#removeUrl(remove.dataset.removeFilter, remove.dataset.removeValue);
      if (this.dataset.mode === 'native') location.assign(url.href);
      else { this.#visitSample(url); this.#focusAfterChip(index); }
      return;
    }
    if (event.target.closest('[data-clear-filters]')) {
      event.preventDefault();
      if (this.#dialog?.open) { this.#resetFilterFields(); this.#updateDraftCount(); return; }
      const url = this.#clearUrl();
      if (this.dataset.mode === 'native') location.assign(url.href);
      else { this.#visitSample(url); this.#focusFilterEntry(); }
      return;
    }
    if (event.target.closest('[data-clear-search]')) {
      event.preventDefault();
      const input = this.querySelector('[data-catalog-query]');
      if (!input) return;
      input.value = '';
      if (this.dataset.mode === 'native') this.#form.requestSubmit(); else this.#apply(true);
      input.focus();
      return;
    }
    if (event.target.closest('[data-edit-search]')) { event.preventDefault(); this.querySelector('[data-catalog-query]')?.focus(); }
  }

  #filterFields() { return this.#content ? [...this.#content.querySelectorAll('input[name], select[name]')] : []; }

  #resetFilterFields() {
    for (const input of this.#filterFields()) {
      if (input.type === 'checkbox' || input.type === 'radio') input.checked = false; else input.value = '';
      input.setCustomValidity('');
    }
  }

  #syncFilterLayout() {
    if (!this.#content || !this.#panel || !this.#dialog || typeof this.#dialog.showModal !== 'function') return;
    const trigger = this.querySelector('[data-open-filters]');
    if (this.#mobile.matches) {
      this.#dialog.querySelector('[data-filter-dialog-slot]').append(this.#content);
      this.#panel.hidden = true;
      if (trigger) trigger.hidden = false;
    } else {
      this.#panel.append(this.#content);
      this.#panel.hidden = false;
      if (trigger) trigger.hidden = true;
      this.#closeFilters(false);
    }
  }

  #openFilters(trigger) {
    if (!this.#mobile.matches || !this.#dialog || this.#dialog.open) return;
    this.#snapshot = this.#filterFields().map(input => ({ input, value: input.value, checked: input.checked }));
    this.#committed = false;
    this.#opener = trigger;
    this.#previousOverflow = document.documentElement.style.overflow;
    this.#dialog.showModal();
    this.#session = true;
    document.documentElement.style.overflow = 'hidden';
    trigger.setAttribute('aria-expanded', 'true');
    this.#updateDraftCount();
    this.querySelector('[data-close-filters]')?.focus();
  }

  #closeFilters(commit) {
    if (!this.#dialog?.open) return;
    this.#committed = commit;
    this.#dialog.close();
    this.#finishDialogClose();
  }

  #finishDialogClose() {
    if (!this.#session) return;
    this.#session = false;
    if (!this.#committed) for (const { input, value, checked } of this.#snapshot) { input.value = value; input.checked = checked; input.setCustomValidity(''); }
    this.#snapshot = [];
    document.documentElement.style.overflow = this.#previousOverflow || '';
    this.#opener?.setAttribute('aria-expanded', 'false');
    this.#updateDraftCount();
    if (this.isConnected) this.#focusFilterEntry();
  }

  #focusFilterEntry() {
    const trigger = this.querySelector('[data-open-filters]');
    const target = this.#mobile?.matches && trigger && !trigger.hidden ? trigger : this.#panel?.querySelector('summary');
    target?.focus({ preventScroll: true });
  }

  #focusAfterChip(index) {
    const chips = [...this.querySelectorAll('[data-remove-filter]')];
    if (chips.length) chips[Math.min(index, chips.length - 1)].focus({ preventScroll: true }); else this.#focusFilterEntry();
  }

  #validateRange() {
    const min = this.#content?.querySelector('[data-price-bound="min"]');
    const max = this.#content?.querySelector('[data-price-bound="max"]');
    max?.setCustomValidity('');
    if (min?.value !== '' && max?.value !== '' && min && max && Number(min.value) > Number(max.value)) max.setCustomValidity(this.dataset.rangeError || 'Maximum price must be at least the minimum price.');
    return this.#form.reportValidity();
  }

  #read() {
    const params = new URL(location.href).searchParams;
    for (const name of ['gift_type', 'price_min', 'price_max', 'sort_by']) {
      const field = this.#form.querySelector(`[name="${name}"]`);
      if (!field) continue;
      let value = params.get(name) || (name === 'sort_by' ? 'manual' : '');
      if (name.startsWith('price_') && value !== '' && (!Number.isFinite(Number(value)) || Number(value) < 0)) value = '';
      field.value = value;
      if (name === 'sort_by' && !field.value) field.value = 'manual';
    }
    const query = this.querySelector('[data-catalog-query]');
    if (query) query.value = params.get('q') || '';
  }

  #visitSample(url) {
    if (url.href !== location.href) history.pushState({}, '', url);
    this.#read();
    this.#apply(false);
  }

  #clearUrl() {
    const url = new URL(location.href);
    for (const key of [...url.searchParams.keys()]) if (key.startsWith('filter.') || ['gift_type', 'price_min', 'price_max', 'page'].includes(key)) url.searchParams.delete(key);
    return url;
  }

  #removeUrl(name, value) {
    const url = new URL(location.href);
    const priceBound = this.#filterFields().some(input => input.name === name && input.matches('[data-price-bound]'));
    const remaining = priceBound ? [] : url.searchParams.getAll(name).filter(item => item !== value);
    url.searchParams.delete(name);
    for (const item of remaining) url.searchParams.append(name, item);
    url.searchParams.delete('page');
    return url;
  }

  #entries() {
    const entries = [];
    for (const input of this.#filterFields()) {
      if (input.disabled || !input.value || ((input.type === 'checkbox' || input.type === 'radio') && !input.checked)) continue;
      let label = input.dataset.chipLabel || input.name;
      if (input.matches('select')) label += `: ${input.selectedOptions[0]?.textContent || input.value}`;
      else if (input.matches('[data-price-bound]')) {
        let price = input.value;
        try { price = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(Number(input.value)); } catch { /* Keep the entered numeric amount. */ }
        label += `: ${price}`;
      }
      entries.push({ name: input.name, value: input.value, label });
    }
    return entries;
  }

  #updateDraftCount() {
    const count = this.querySelector('[data-draft-count]');
    if (count) count.textContent = (this.dataset.selectedLabel || '__COUNT__ selected').replace('__COUNT__', String(this.#entries().length));
  }

  #refreshAppliedState() {
    const entries = this.#entries();
    const list = this.querySelector('[data-filter-chips]');
    if (list) {
      list.replaceChildren();
      for (const entry of entries) {
        const chip = document.createElement('a'); chip.className = 'nr-filter-chip';
        chip.href = this.#removeUrl(entry.name, entry.value).href;
        chip.dataset.removeFilter = entry.name; chip.dataset.removeValue = entry.value;
        chip.setAttribute('aria-label', (this.dataset.removeLabel || 'Remove __FILTER__').replace('__FILTER__', entry.label));
        const text = document.createElement('span'); text.textContent = entry.label;
        const cross = document.createElement('span'); cross.textContent = '×'; cross.setAttribute('aria-hidden', 'true');
        chip.append(text, cross); list.append(chip);
      }
    }
    const active = this.querySelector('[data-active-filters]');
    if (active) active.hidden = !entries.length;
    this.querySelectorAll('[data-filter-total]').forEach(count => { count.textContent = String(entries.length); });
    this.querySelectorAll('[data-clear-filters]').forEach(link => { link.href = this.#clearUrl().href; link.hidden = !entries.length && !this.#content.contains(link); });
    const query = this.querySelector('[data-catalog-query]')?.value.trim() || '';
    const display = this.querySelector('[data-query-display]'); if (display) display.textContent = query;
    const line = this.querySelector('[data-query-line]'); if (line) line.hidden = !query;
    const hint = this.querySelector('[data-query-hint]'); if (hint) hint.hidden = !!query;
    const clear = this.querySelector('[data-clear-search]'); if (clear) { const url = new URL(location.href); url.searchParams.delete('q'); url.searchParams.delete('page'); clear.href = url.href; clear.hidden = !query; }
    this.#updateDraftCount();
  }

  #apply(updateUrl) {
    if (!this.#validateRange()) return;
    const type = this.#form.querySelector('[data-type-filter]').value;
    const minInput = this.#form.querySelector('[data-price-min]').value;
    const maxInput = this.#form.querySelector('[data-price-max]').value;
    const min = Number(minInput || 0) * 100;
    const max = maxInput === '' ? Infinity : Number(maxInput) * 100;
    const sort = this.#form.querySelector('[data-sort]').value;
    const queryInput = this.querySelector('[data-catalog-query]');
    const query = queryInput ? queryInput.value.trim() : this.dataset.query || '';
    const tokens = query.toLocaleLowerCase().replace(/[^\p{L}\p{N}\s-]/gu, ' ').split(/\s+/).filter(token => token && !['gift', 'gifts', 'personalized', 'personalised', 'for', 'the', 'a'].includes(token));
    const grid = this.querySelector('[data-product-grid]');
    const cards = [...grid.querySelectorAll('.nr-card')];
    let count = 0;
    for (const card of cards) {
      const haystack = `${card.dataset.title} ${card.dataset.keywords} ${card.dataset.giftType}`.toLocaleLowerCase();
      const matches = tokens.every(token => haystack.includes(token) || (token.endsWith('s') && token.length > 4 && haystack.includes(token.slice(0, -1))));
      card.hidden = !(matches && (!type || card.dataset.giftType === type) && Number(card.dataset.price) >= min && Number(card.dataset.price) <= max);
      if (!card.hidden) count++;
    }
    cards.sort((a, b) => sort === 'price-ascending' ? Number(a.dataset.price) - Number(b.dataset.price) : sort === 'price-descending' ? Number(b.dataset.price) - Number(a.dataset.price) : sort === 'title-ascending' ? a.dataset.title.localeCompare(b.dataset.title) : Number(a.dataset.original) - Number(b.dataset.original));
    const empty = this.querySelector('[data-catalog-empty]');
    cards.forEach(card => grid.insertBefore(card, empty));
    empty.hidden = count > 0;
    this.querySelector('[data-result-count]').textContent = (this.dataset.resultLabel || '__COUNT__ gifts').replace('__COUNT__', String(count));
    if (updateUrl) {
      const url = new URL(location.href);
      for (const [key, value] of [['gift_type', type], ['price_min', minInput], ['price_max', maxInput], ['sort_by', sort === 'manual' ? '' : sort]]) value ? url.searchParams.set(key, value) : url.searchParams.delete(key);
      if (this.querySelector('[data-catalog-query]')) query ? url.searchParams.set('q', query) : url.searchParams.delete('q');
      url.searchParams.delete('page');
      if (url.href !== location.href) history.pushState({}, '', url);
    }
    this.#refreshAppliedState();
  }
}
class RetailSample extends Component {
  #controller; #objectUrl;
  connectedCallback(){super.connectedCallback();this.#controller=new AbortController();const options={signal:this.#controller.signal};this.querySelectorAll('[data-gallery-src]').forEach((button,index)=>button.addEventListener('click',()=>{this.querySelector('[data-main-photo]').src=button.dataset.gallerySrc;this.querySelector('[data-gallery-counter]').textContent=`${index+1} / ${this.querySelectorAll('[data-gallery-src]').length}`;this.querySelectorAll('[data-gallery-src]').forEach(b=>b.classList.toggle('is-active',b===button));},options));
    this.querySelector('[data-photo-preview]')?.addEventListener('change',event=>{const file=event.target.files[0],image=this.querySelector('[data-upload-preview]');if(this.#objectUrl)URL.revokeObjectURL(this.#objectUrl);image.hidden=true;if(file&&file.type.startsWith('image/')){this.#objectUrl=URL.createObjectURL(file);image.src=this.#objectUrl;image.hidden=false;}},options);
  }
  disconnectedCallback(){super.disconnectedCallback();this.#controller?.abort();if(this.#objectUrl)URL.revokeObjectURL(this.#objectUrl);}
}
class RetailSaved extends Component {
  #controller;
  connectedCallback(){super.connectedCallback();this.#controller=new AbortController();const options={signal:this.#controller.signal};document.addEventListener('nr:saved',()=>this.#render(),options);this.addEventListener('click',event=>{const remove=event.target.closest('[data-remove-saved]');if(remove){const next=savedItems().filter(x=>x.url!==remove.dataset.removeSaved);if(writeSaved(next)){document.dispatchEvent(new CustomEvent('nr:saved-message',{detail:{action:'removed'}}));(this.querySelector('[data-remove-saved]')||this.querySelector('[data-saved-empty] a'))?.focus();}}if(event.target.closest('[data-saved-clear]')&&writeSaved([])){document.dispatchEvent(new CustomEvent('nr:saved-message',{detail:{action:'cleared'}}));this.querySelector('[data-saved-empty] a')?.focus();}},options);this.#render();}
  disconnectedCallback(){super.disconnectedCallback();this.#controller?.abort();}
  #render(){const grid=this.querySelector('[data-saved-grid]');grid.replaceChildren();const items=savedItems();this.querySelector('[data-saved-empty]').hidden=items.length>0;this.querySelector('[data-saved-clear]').hidden=!items.length;const count=this.querySelector('[data-local-count]');if(count)count.textContent=String(items.length);for(const item of items){const card=document.createElement('article');card.className='nr-card';const media=document.createElement('div');media.className='nr-card__media';const link=document.createElement('a');link.href=item.url;const img=document.createElement('img');img.src=item.image;img.alt=item.title;img.width=800;img.height=800;img.loading='lazy';link.append(img);media.append(link);if(item.sample){const badge=document.createElement('span');badge.className='nr-card__sample';badge.textContent=this.dataset.sampleLabel;media.append(badge);}const title=document.createElement('a');title.href=item.url;title.className='nr-card__title';title.textContent=item.title;card.append(media,title);if(item.currency&&item.price!==null){try{const price=document.createElement('div');price.className='nr-card__price';price.textContent=new Intl.NumberFormat(document.documentElement.lang||'en',{style:'currency',currency:item.currency,currencyDisplay:'code'}).format(item.price/100);card.append(price);}catch{}}const view=document.createElement('a');view.className='nr-saved__view';view.href=item.url;view.textContent=item.sample?this.dataset.viewSample:this.dataset.viewGift;view.setAttribute('aria-label',`${view.textContent}: ${item.title}`);card.append(view);const remove=document.createElement('button');remove.type='button';remove.className='nr-saved__remove';remove.dataset.removeSaved=item.url;remove.textContent=this.dataset.remove;remove.setAttribute('aria-label',`${this.dataset.remove}: ${item.title}`);card.append(remove);grid.append(card);}}
}
for(const [name,constructor] of [['nr-carousel',RetailCarousel],['nr-header',RetailHeader],['nr-store',RetailStore],['nr-catalog',RetailCatalog],['nr-sample',RetailSample],['nr-saved',RetailSaved]])if(!customElements.get(name))customElements.define(name,constructor);
